import {useProductSubscription} from "./useProductSubscription";
import {useProductQuantity} from "./useProductQuantity";
import {useProductOptionsV2} from "./useProductOptionsV2";
import {useAPIWrapper} from "./useAPIWrapper";
import {getPrice} from "@/utils/get-price";
import {optimisticFromProduct} from "@/utils/optimistic-line";
import {ref, onMounted, watch} from "vue";
import {useStore} from "vuex";

// All the UI responsibility has been removed from here.
// Like: the Cart opening when you call the addToCart function.
// This should be implemented on the UI components that will use this composables.

const useProductForm = ({
  product,
  defaultQuantity,
  defaultVariantId,
  defaultSellingPlanId,
}) => {
  // This will change when we add Product Options.
  const id = defaultVariantId ?? product?.variants[0]?.id;
  const currentVariantId = ref(id);

  const available = ref(
    product.available && product?.variants?.some(v => v.available),
  );

  const productQuantity = useProductQuantity(defaultQuantity);
  const productSubscription = useProductSubscription(
    product,
    defaultSellingPlanId,
  );
  const productOptions = useProductOptionsV2(product, id);
  const currentPrice = ref(
    getPrice(
      product,
      id,
      productQuantity.quantity,
      productSubscription.sellingPlanId.value,
    ),
  );
  const shouldUpdateProductURL = product.variants.length > 1;

  // When product option changes, variant changes.

  watch(productOptions.selectedVariant, (value, oldValue) => {
    if (value !== oldValue) {
      currentVariantId.value = value ? value.id : null;
      available.value = value ? value.available : false;

      if (shouldUpdateProductURL) {
        updateURLWithVariant(currentVariantId.value);
      }

      currentPrice.value = getPrice(
        product,
        currentVariantId.value,
        productQuantity.quantity,
        productSubscription.sellingPlanId.value,
      );
    }
  });
  watch(productSubscription.sellingPlanId, (value, oldValue) => {
    if (value !== oldValue) {
      currentPrice.value = getPrice(
        product,
        currentVariantId.value,
        productQuantity.quantity,
        value,
      );
    }
  });

  // ?variant=SOME_VARIANT_ID
  // ?otp=true --- Subscription is enabled by defaul, when possible.
  onMounted(() => {
    const searchParams = new URLSearchParams(location.search);

    const preferOTP = JSON.parse(searchParams.get("otp"));
    const variantId = JSON.parse(searchParams.get("variant"));

    if (preferOTP) {
      productSubscription.setSubscriptionActive(false);
    }

    if (variantId) {
      currentVariantId.value = variantId;
      productOptions.setOptionsByVariantId(currentVariantId.value);
    }
  });

  const store = useStore();

  /**
   * Goes through `cart/addItem` rather than calling the endpoint directly. That
   * is what puts the line in the cart and opens the drawer straight away, with
   * the request running behind it — calling `fetchAddItem` here meant the
   * product only appeared once the response came back, and then only after the
   * caller had fetched the whole cart again on top of it.
   */
  const addToCartFn = override => {
    const id = override?.id ?? currentVariantId.value;
    const quantity = override?.quantity ?? productQuantity.quantity.value;
    const selling_plan =
      override?.selling_plan ?? productSubscription.sellingPlanId.value;
    const variant = (product?.variants || []).find(v => v.id == id);

    return store.dispatch("cart/addItem", {
      id,
      quantity,
      ...(selling_plan ? {selling_plan} : {}),
      _optimistic: optimisticFromProduct(product, variant, selling_plan),
    });
  };

  const updateURLWithVariant = variantId => {
    const url = new URL(location);
    if (variantId) {
      url.searchParams.set("variant", variantId);
    } else {
      url.searchParams.delete("variant");
    }
    history.pushState("", "", url);
  };

  const {isLoading, error, execute: addToCart} = useAPIWrapper(addToCartFn);

  return {
    available,
    id: currentVariantId,
    ...productQuantity,
    ...productSubscription,
    ...productOptions,
    isLoading,
    error,
    addToCart,
    currentPrice,
    getPrice,
  };
};

export default useProductForm;
